import React, { useState } from 'react';
import { AlertCircle, CheckCircle, X, Ticket } from 'lucide-react';
import api from '../api/client';

export default function TicketAcknowledgementModal({ tickets, onDone }) {
  const [acknowledging, setAcknowledging] = useState({});
  const [acknowledged, setAcknowledged] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || tickets.length === 0) return null;

  const remaining = tickets.filter(t => !acknowledged.includes(t.id));

  if (remaining.length === 0) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="glass glow-border rounded-2xl p-6 text-center" style={{ maxWidth: 400, width: '90%' }}>
          <CheckCircle size={40} style={{ color: '#34d399', margin: '0 auto 12px' }}/>
          <div className="font-semibold mb-1" style={{ color: '#e2e8f0' }}>All tickets acknowledged!</div>
          <div className="text-xs mb-4" style={{ color: '#475569' }}>Tickets are now In Progress. You can view them in the IT Tickets module.</div>
          <button onClick={onDone} className="btn-primary w-full justify-center">Continue</button>
        </div>
      </div>
    );
  }

  const handleAck = async (id) => {
    setAcknowledging(a => ({ ...a, [id]: true }));
    try {
      await api.post(`/tickets/${id}/acknowledge`);
      setAcknowledged(prev => [...prev, id]);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to acknowledge');
    } finally {
      setAcknowledging(a => ({ ...a, [id]: false }));
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="glass glow-border rounded-2xl" style={{ maxWidth: 520, width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)' }}>
              <AlertCircle size={16} style={{ color: '#fbbf24' }}/>
            </div>
            <div>
              <div className="font-semibold text-sm" style={{ color: '#e2e8f0' }}>New Tickets Assigned to You</div>
              <div className="text-xs" style={{ color: '#475569' }}>Please acknowledge each ticket to confirm receipt</div>
            </div>
          </div>
          <button onClick={() => setDismissed(true)} className="btn-ghost p-1.5" title="Dismiss — you can acknowledge later in IT Tickets">
            <X size={14} style={{ color: '#475569' }}/>
          </button>
        </div>

        <div className="px-5 pb-2">
          <div className="text-xs px-3 py-2 rounded-lg" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', color: '#fbbf24' }}>
            Acknowledging a ticket confirms you've seen it and automatically sets it to <strong>In Progress</strong>.
          </div>
        </div>

        {/* Ticket list */}
        <div className="overflow-y-auto px-5 pb-5 space-y-3 flex-1">
          {remaining.map(t => (
            <div key={t.id} className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Ticket size={12} style={{ color: '#60a5fa', flexShrink: 0 }}/>
                    <span className="font-mono text-xs font-semibold" style={{ color: '#60a5fa' }}>{t.ticket_number}</span>
                    <span className="text-xs px-1.5 py-0.5 rounded capitalize" style={{ background: 'rgba(245,158,11,0.1)', color: '#fbbf24', fontSize: 10 }}>{t.priority}</span>
                  </div>
                  <div className="text-sm font-medium mb-1" style={{ color: '#e2e8f0' }}>{t.title}</div>
                  <div className="text-xs" style={{ color: '#475569' }}>
                    Reported by: {t.reportedBy?.full_name || '—'}
                    {t.category && <> · {t.category.replace(/_/g, ' ')}</>}
                  </div>
                </div>
                <button
                  onClick={() => handleAck(t.id)}
                  disabled={acknowledging[t.id]}
                  className="btn-primary text-xs px-3 py-1.5 flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg,#10b981,#059669)', minWidth: 100 }}>
                  {acknowledging[t.id] ? 'Processing...' : 'Acknowledge'}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="px-5 pb-5">
          <button onClick={() => setDismissed(true)} className="btn-ghost w-full justify-center text-xs" style={{ color: '#475569' }}>
            Dismiss — I'll acknowledge later in IT Tickets
          </button>
        </div>
      </div>
    </div>
  );
}
